/** Pure markdown rendering shared by CLI and tests. Tokens per task are averaged here from server-provided case means. */
import type {
  AgentEvalCaseResult,
  AgentEvalCompareResult,
  AgentEvalRunResult,
  AgentEvalTokens,
} from './agent-eval-result.js';

const cell = (value: string) => value.replaceAll('|', '\\|').replace(/\r?\n/g, ' ');
const percent = (value: number) => `${Math.round(value * 100)}%`;
const money = (cents: number | null, incomplete = false) =>
  cents === null ? 'unpriced' : `$${(cents / 100).toFixed(2)}${incomplete ? '*' : ''}`;
const minutes = (ms: number) => (ms / 60_000).toFixed(1);
const tokenCount = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : `${Math.round(n)}`;
const tokenPair = (tokens: AgentEvalTokens) =>
  `${tokenCount(tokens.input)} / ${tokenCount(tokens.output)}`;
/** Mean tokens per task, averaged from the server's per-case means; shared by the CLI markdown and the run page. */
export const meanTaskTokens = (result: AgentEvalRunResult): AgentEvalTokens => {
  const cases = result.cases;
  const mean = (key: keyof AgentEvalTokens) =>
    cases.length ? cases.reduce((n, c) => n + c.tokensPerAttempt[key], 0) / cases.length : 0;
  return {
    input: mean('input'),
    output: mean('output'),
    cacheRead: mean('cacheRead'),
    cacheCreation: mean('cacheCreation'),
  };
};
const verdict = (r: AgentEvalRunResult) => (r.verdict ?? r.status).toUpperCase();

export function renderAgentEvalRun(result: AgentEvalRunResult): string {
  const s = result.summary;
  const taskTokens = meanTaskTokens(result);
  const warning = result.warnings.length
    ? ` [WARNING: ${result.warnings.map(cell).join('; ')}]`
    : '';
  // userSim is non-null exactly for a multi-turn eval (D16); single-turn output stays unchanged.
  const multiTurn = s.userSim !== null;
  const turnsToSuccess = (c: AgentEvalCaseResult) => {
    const passing = c.attempts.flatMap((a) => (a.passed && a.turns !== null ? [a.turns] : []));
    return passing.length ? (passing.reduce((n, t) => n + t, 0) / passing.length).toFixed(1) : '-';
  };
  const lines = [
    `## Eval ${cell(result.eval)}: ${verdict(result)} (${s.casesPassed}/${s.cases} cases, bar ${percent(result.passBar)}) — ${cell(result.model.alias)} (${cell(result.model.id)}) @ ${result.commit.slice(0, 7)}, ${result.repeats} repeats${warning}`,
    '',
    `| Case | Result | Attempts | Score | ${multiTurn ? 'Turns | ' : ''}Cost/attempt | Time/attempt | Tokens in/out |`,
    `|---|---|---|---|${multiTurn ? '---|' : ''}---|---|---|`,
    ...result.cases.map(
      (c) =>
        `| ${cell(c.key)} | ${c.passed ? 'PASS' : 'FAIL'} | ${c.passedAttempts}/${c.attempts.length} | ${c.meanScore.toFixed(1)} | ${multiTurn ? `${turnsToSuccess(c)} | ` : ''}${money(
          c.costCentsPerAttempt,
          c.attempts.some((a) => !a.costComplete)
        )} | ${minutes(c.durationMsPerAttempt)} min | ${tokenPair(c.tokensPerAttempt)} |`
    ),
    '',
    `Per task: ${money(s.costCentsPerTask, s.incompleteCalls > 0)} | ${minutes(s.durationMsPerTask)} min | ${tokenCount(taskTokens.input)} in / ${tokenCount(taskTokens.output)} out. Total: ${money(s.costCents, s.incompleteCalls > 0)} for ${s.attempts} attempts, ${Math.round(result.cases.flatMap((c) => c.attempts).reduce((n, a) => n + a.durationMs, 0) / 60_000)} min of agent time.`,
  ];
  const judges = [
    ...new Set(
      result.cases
        .flatMap((c) => c.attempts.flatMap((a) => a.grades))
        .filter((g) => g.kind === 'ai-judge')
        .map((g) => g.judgeModel)
    ),
  ];
  lines.push(
    `Judge (${judges.length ? judges.map((m) => cell(m ?? 'unknown')).join(', ') : 'none'}, pinned): ${s.judge.calls} calls, ${money(s.judge.costCents)}, not included above.`
  );
  if (s.userSim) {
    const models = [
      ...new Set(
        result.cases.flatMap((c) =>
          c.attempts.flatMap((a) => a.calls.filter((call) => call.role === 'user-sim').map((call) => call.model))
        )
      ),
    ];
    lines.push(
      `User-sim (${models.length ? models.map(cell).join(', ') : 'none'}, pinned): ${s.userSim.calls} calls, ${money(s.userSim.costCents)}, not included above.`
    );
  }
  const failures = result.cases.flatMap((c) =>
    c.attempts.flatMap((a) => [
      ...a.grades
        .filter((g) => !g.pass)
        .map((g) => `${cell(c.key)} #${a.index}: ${cell(g.grader)}: ${cell(g.reason)}`),
      ...(a.error && !a.grades.some((g) => !g.pass && (g.reason === a.error || g.error === a.error))
        ? [`${cell(c.key)} #${a.index}: ${cell(a.error)}`]
        : []),
    ])
  );
  lines.push(`Failed grades: ${failures.length ? failures.join('; ') : 'none'}.`);
  if (result.error) lines.push(`Error: ${cell(result.error)}.`);
  if (s.incompleteCalls) lines.push('* Cost is a lower bound because agent calls were incomplete.');
  lines.push(
    `Warnings: ${result.warnings.length ? result.warnings.map(cell).join('; ') : 'none'}. Run: ${result.id}.`
  );
  return `${lines.join('\n')}\n`;
}

export function renderAgentEvalCompare(result: AgentEvalCompareResult): string {
  const lines = [
    `## Eval compare: ${cell(result.eval)} @ ${result.commit.slice(0, 7)}${result.batchId ? ` (batch ${result.batchId})` : ''}`,
    '',
    '| Model | Verdict | Pass rate | Mean score | Cost/task | Time/task | Tokens/task in/out |',
    '|---|---|---|---|---|---|---|',
    ...result.runs.map(
      (r) =>
        `| ${cell(r.model.alias)} (${cell(r.model.id)}) | ${verdict(r)} | ${percent(r.summary.passRate)} | ${r.summary.meanScore.toFixed(1)} | ${money(r.summary.costCentsPerTask, r.summary.incompleteCalls > 0)} | ${minutes(r.summary.durationMsPerTask)} min | ${tokenPair(meanTaskTokens(r))} |`
    ),
    '',
    `| Case | ${result.runs.map((r) => cell(r.model.alias)).join(' | ')} |`,
    `|---|${result.runs.map(() => '---|').join('')}`,
    ...result.cases.map(
      (c) =>
        `| ${cell(c.key)} | ${c.models
          .map(
            ({ result: r }) =>
              `${r.passedAttempts}/${r.attempts.length} ${r.passed ? 'PASS' : 'FAIL'} ${money(
                r.costCentsPerAttempt,
                r.attempts.some((a) => !a.costComplete)
              )} ${minutes(r.durationMsPerAttempt)}m`
          )
          .join(' | ')} |`
    ),
  ];
  for (const r of result.runs)
    if (r.warnings.length)
      lines.push(`Warnings (${cell(r.model.alias)}): ${r.warnings.map(cell).join('; ')}.`);
  if (result.runs.some((r) => r.summary.incompleteCalls > 0))
    lines.push('* Cost is a lower bound because agent calls were incomplete.');
  return `${lines.join('\n')}\n`;
}
